import { useState } from 'react';
import { Recipe } from '@/types/api';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Heart, 
  Clock, 
  Users, 
  Plus,
  Wifi,
  WifiOff
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHybridFavorites, useEnsureMealPlan } from '@/hooks';
import MealPlannerModal from '@/components/meal-planner/MealPlannerModal';

interface RecipeCardProps {
  recipe: Recipe;
  onFavoriteToggle?: (recipe: Recipe) => void; // Optional - if not provided, uses internal logic
  onAddToMealPlan?: (recipe: Recipe) => void;
  onViewRecipe?: (recipe: Recipe) => void;
  isFavorite?: boolean; // Optional - if not provided, uses internal logic
  className?: string;
  variant?: 'default' | 'compact';
  showSyncStatus?: boolean; // Show offline/sync indicators
}

export function RecipeCard({
  recipe,
  onFavoriteToggle,
  onViewRecipe,
  isFavorite: propIsFavorite,
  className,
  variant = 'default',
  showSyncStatus = false
}: RecipeCardProps) {
  
  // Use hybrid favorites hook
  const favoritesHook = useHybridFavorites();
  
  // Use internal logic if no external handlers provided
  const isFavorite = propIsFavorite !== undefined 
    ? propIsFavorite 
    : favoritesHook.isFavorite(recipe.id);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (onFavoriteToggle) {
      // Use external handler if provided (backward compatibility)
      onFavoriteToggle(recipe);
    } else {
      // Use internal hybrid logic
      try {
        await favoritesHook.toggleFavorite(recipe.id);
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
        // Could show a toast notification here
      }
    }
  };

  const handleAddToMealPlan = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Always open MealSchedule modal (new UX)
    setScheduleOpen(true);
  };

  const handleViewRecipe = () => {
    onViewRecipe?.(recipe);
  };

  const displayTags = recipe.tags?.slice(0, 3) || [];
  const isCompact = variant === 'compact';

  // Meal schedule modal state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const ensureMealPlan = useEnsureMealPlan();

  const handleConfirmSchedule = async ({ dateISO, service }: { dateISO: string; service: 'comida' | 'cena' }) => {
    try {
      await ensureMealPlan.mutateAsync({
        recipeId: recipe.id,
        dateISO,
        service,
      });
    } catch (err) {
      console.error('Failed to schedule meal:', err);
      // Could show a toast notification here with error details
    } finally {
      setScheduleOpen(false);
    }
  };

  return (
    <Card 
      className={cn(
        "group cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1",
        "border-0 shadow-sm bg-white",
        className
      )}
      onClick={handleViewRecipe}
    >
      {/* Image Container */}
      <div className={cn(
        "relative overflow-hidden rounded-t-lg",
        isCompact ? "aspect-[4/3]" : "aspect-[3/2]"
      )}>
        {/* Recipe Image */}
        <OptimizedImage
          recipe={recipe}
          alt={recipe.title}
          className={cn(
            "w-full h-full object-cover transition-transform duration-200",
            "group-hover:scale-105"
          )}
          fallbackClassName="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
        />

        {/* Overlay Actions */}
        <div className="absolute inset-0 bg-black/10 md:bg-black/0 md:group-hover:bg-black/20 transition-colors duration-200">
          <div className="absolute top-2 right-2 flex gap-1">
            {/* Favorite Button */}
            <Button
              size="sm"
              variant="secondary"
              className={cn(
                "h-8 w-8 p-0 rounded-full transition-all duration-200",
                // Always visible on mobile, hover on desktop
                "opacity-100 md:opacity-0 md:group-hover:opacity-100",
                "translate-y-0 md:translate-y-2 md:group-hover:translate-y-0",
                // Better contrast on mobile
                "bg-white/95 md:bg-white/90 backdrop-blur-sm hover:bg-white",
                "shadow-md md:shadow-none",
                isFavorite && "text-red-500 hover:text-red-600"
              )}
              onClick={handleFavoriteClick}
            >
              <Heart 
                className={cn(
                  "h-4 w-4 transition-colors",
                  isFavorite && "fill-current"
                )} 
              />
            </Button>
          </div>

          <div className="absolute bottom-2 right-2">
            {/* Add to Meal Plan Button */}
            <Button
              size="sm"
              variant="secondary"
              className={cn(
                "h-8 w-8 p-0 rounded-full transition-all duration-200",
                // Always visible on mobile, hover on desktop
                "opacity-100 md:opacity-0 md:group-hover:opacity-100",
                "translate-y-0 md:translate-y-2 md:group-hover:translate-y-0",
                // Better contrast on mobile
                "bg-white/95 md:bg-white/90 backdrop-blur-sm hover:bg-white hover:bg-primary hover:text-white",
                "shadow-md md:shadow-none"
              )}
              onClick={handleAddToMealPlan}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Recipe Source Badge */}
        {recipe.source && (
          <Badge 
            variant="secondary" 
            className="absolute top-2 left-2 text-xs bg-black/50 text-white border-0"
          >
            {recipe.source}
          </Badge>
        )}

        {/* Sync Status Indicator */}
        {showSyncStatus && (
          <div className="absolute bottom-2 left-2">
            {!favoritesHook.isOnline ? (
              <Badge variant="secondary" className="text-xs bg-orange-500 text-white border-0">
                <WifiOff className="h-3 w-3 mr-1" />
                Offline
              </Badge>
            ) : favoritesHook.pendingChanges.includes(recipe.id) ? (
              <Badge variant="secondary" className="text-xs bg-blue-500 text-white border-0">
                <Wifi className="h-3 w-3 mr-1" />
                Sincronizando...
              </Badge>
            ) : null}
          </div>
        )}
      </div>

      {/* Card Content */}
      <CardContent className={cn("p-4", isCompact && "p-3")}>
        {/* Recipe Title */}
        <h3 className={cn(
          "font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight",
          isCompact ? "text-sm" : "text-base"
        )}>
          {recipe.title}
        </h3>

        {/* Recipe Meta Info */}
        <div className="flex items-center gap-3 text-gray-500 text-sm mb-3">
          {recipe.timeMinutes && (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>{recipe.timeMinutes}min</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            <span>{recipe.servings} personas</span>
          </div>
        </div>

        {/* Recipe Tags */}
        {!isCompact && displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {displayTags.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                className="text-xs px-2 py-0.5 h-auto border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                {tag.tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
      <MealPlannerModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onConfirm={handleConfirmSchedule}
        recipe={recipe}
      />
    </Card>
  );
}

